/* /portaal/planning — mobile-first weekplanning per kind met brengtijd/
   ophaaltijd, afwezigheid en sluitdagen. Toont deze week + volgende week. */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, PlaneTakeoff, XCircle, CheckCircle2 } from "lucide-react";

const DAG_NAMEN = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const MND_KORT = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function iso(d) { return d.toISOString().slice(0, 10); }

export default function PortaalPlanningPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [selKid, setSelKid] = useState(null);
  const [presentieBy, setPresentieBy] = useState({}); // iso → doc
  const [sluitdagen, setSluitdagen] = useState({}); // iso → doc
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = useMemo(() => addDays(startOfWeek(new Date()), weekOffset * 7), [weekOffset]);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    (async () => {
      try {
        const ov = await api.get("/portaal/overview");
        const list = ov.data.kinderen || [];
        setKids(list);
        if (list.length) setSelKid(list[0]);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    })();
  }, [status, user]);

  useEffect(() => {
    if (!selKid) return;
    setLoading(true);
    const from = iso(weekStart);
    const to = iso(addDays(weekStart, 6));
    (async () => {
      try {
        // Ophaal alle presentie voor deze week + kind. Fallback naar per-datum call als geen bulk endpoint bestaat.
        const results = await Promise.all(weekDates.map(d =>
          api.get(`/portaal/kind/${selKid.id}/presentie?datum=${iso(d)}`).catch(() => ({ data: null }))
        ));
        const map = {};
        weekDates.forEach((d, i) => { if (results[i].data) map[iso(d)] = results[i].data; });
        setPresentieBy(map);
        // Sluitdagen: probeer publieke endpoint; anders leeg
        try {
          const r = await api.get(`/sluitdagen?from=${from}&to=${to}`);
          const sm = {};
          (r.data || []).forEach(s => { sm[s.datum] = s; });
          setSluitdagen(sm);
        } catch { setSluitdagen({}); }
      } finally { setLoading(false); }
    })();
  }, [selKid, weekStart]);

  if (status === "loading") return null;
  if (status !== "authed" || user?.role !== "ouder") return <Navigate to="/login" replace />;

  const today = iso(new Date());

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="portaal-planning-page">
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="planning-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="text-base font-semibold text-neutral-900">Planning</div>
          <div className="text-xs text-neutral-500">
            Week {weekStart.getDate()} {MND_KORT[weekStart.getMonth()]} — {addDays(weekStart, 6).getDate()} {MND_KORT[addDays(weekStart, 6).getMonth()]}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w - 1)} data-testid="planning-prev-week">‹</Button>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)} data-testid="planning-this-week">Vandaag</Button>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(w => w + 1)} data-testid="planning-next-week">›</Button>
      </div>

      {kids.length > 1 && (
        <div className="px-4 pt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {kids.map(k => (
            <Button
              key={k.id}
              size="sm"
              variant={selKid?.id === k.id ? "default" : "outline"}
              onClick={() => setSelKid(k)}
              className={selKid?.id === k.id ? "text-white shrink-0" : "shrink-0"}
              style={selKid?.id === k.id ? { background: "var(--sage)" } : {}}
              data-testid={`planning-kid-${k.id}`}
            >{k.voornaam}</Button>
          ))}
        </div>
      )}

      <div className="px-4 pt-4 space-y-2">
        {loading && <div className="text-center text-sm text-neutral-500 py-6">Laden…</div>}
        {!loading && weekDates.map((d, i) => {
          const key = iso(d);
          const isToday = key === today;
          const p = presentieBy[key];
          const sd = sluitdagen[key];
          const isWeekend = i >= 5;
          const isAbsent = p && p.aanwezig === false && (p.absent_reason || p.reden_afmelding);
          const isAangekomen = p && p.aankomst && p.aanwezig !== false;
          return (
            <Card key={key} className={`p-3 flex items-start gap-3 ${isToday ? "bg-emerald-50 border-emerald-200" : "bg-white"}`} data-testid={`planning-day-${key}`}>
              <div className="w-14 text-center shrink-0">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">{DAG_NAMEN[i]}</div>
                <div className={`text-xl font-semibold ${isToday ? "text-emerald-700" : "text-neutral-900"}`}>{d.getDate()}</div>
                <div className="text-[10px] text-neutral-400">{MND_KORT[d.getMonth()]}</div>
              </div>
              <div className="flex-1 min-w-0">
                {sd ? (
                  <div className="text-sm text-red-800" data-testid={`planning-closed-${key}`}>
                    <Badge className="bg-red-100 text-red-800 border border-red-200 mb-1">Gesloten</Badge>
                    <div className="text-xs text-red-700">{sd.reden}</div>
                  </div>
                ) : isAbsent ? (
                  <div className="text-sm">
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200 mb-1"><XCircle className="h-3 w-3 mr-1 inline" />Afgemeld</Badge>
                    <div className="text-xs text-amber-800">{p.absent_reason || p.reden_afmelding}</div>
                  </div>
                ) : isAangekomen ? (
                  <div className="text-sm">
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 mb-1"><CheckCircle2 className="h-3 w-3 mr-1 inline" />Aangekomen</Badge>
                    <div className="text-xs text-neutral-700">
                      Brengtijd: <span className="font-mono">{p.aankomst}</span>
                      {p.vertrek && <> · Opgehaald: <span className="font-mono">{p.vertrek}</span></>}
                    </div>
                  </div>
                ) : p ? (
                  <div className="text-sm">
                    <Badge className="bg-blue-100 text-blue-800 border border-blue-200 mb-1"><CalendarDays className="h-3 w-3 mr-1 inline" />Gepland</Badge>
                    <div className="text-xs text-neutral-600">Dagdeel: {p.dagdeel || "hele dag"}</div>
                  </div>
                ) : (
                  <div className="text-xs text-neutral-400 italic pt-1">{isWeekend ? "Weekend" : "Geen opvang"}</div>
                )}
              </div>
              {isToday && <Badge className="bg-emerald-700 text-white text-[10px] uppercase tracking-widest shrink-0">Vandaag</Badge>}
            </Card>
          );
        })}
      </div>

      <div className="mt-6 mx-4 p-3 rounded-md bg-white border border-neutral-200 text-xs text-neutral-600 flex items-start gap-2" data-testid="planning-tip">
        <PlaneTakeoff className="h-4 w-4 text-neutral-400 shrink-0 mt-0.5" />
        <div>Ziek of vakantie? Meld je kind af via de Home-pagina — dan zien de leidsters het direct.</div>
      </div>
    </div>
  );
}
